/**
 * MicroFlow — eine Kette in festen Slots.
 *
 * Regeln aus design-hig.md ③:
 *  - Die Slot-NAMEN stehen genau einmal, in `MicroFlowHeader`. Die Zeilen
 *    zeigen nur Form (+ Zahl). Bei 4 Runs × 9 Slots sind das 9 statt 36 Labels.
 *  - Fehlt ein Slot im Objekt, bleibt er stehen und wird zur Ghost-Linie —
 *    dadurch springt beim Tab-Wechsel (Queue) keine Spalte.
 *  - Die Verbindungslinie wird als ZWEI HALBSEGMENTE IN DEN LÜCKEN gezeichnet
 *    (`::before`/`::after` + `--flow-gap`, siehe flow.css) und nicht
 *    durchgezogen und mit einer opaken Scheibe maskiert. Eine Maske in
 *    Hintergrundfarbe bricht sichtbar, sobald die Zeile einen translucenten
 *    Hover-Grund bekommt. Das ist ein Bugfix, kein Stilpunkt.
 *  - Roll-up-Glyphe in der Leitspalte jeder Zeile (`failed > sealed > running >
 *    open > ok`) — beantwortet „welche Zeile ist kaputt" vor dem Kettenlesen.
 *  - Popover je Kette statt nur `title`: auf Touch fällt der Statustext sonst aus.
 *
 * Markup-Regel: eine Zeile ist ein `<div>`, in dem nur der Kopf ein `<button>`
 * ist. Kette und sekundäre Aktionen sind Buttons DANEBEN, nie darin.
 */
import { useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Popover, cx } from "@conuti-das/prince-ui";
import type { FlowState } from "@/types/pipeline";
import { FlowHead } from "./FlowHead";
import { StateGlyph } from "./StateGlyph";
import {
  formatCount,
  rollUpState,
  stateWord,
  type FlowVocabulary,
} from "./flowState";
import "./flow.css";

/** Ein fester Slot der Kette. `full` ist der ausgeschriebene Name (Tooltip). */
export interface FlowSlotDef {
  id: string;
  /** Kurzform für die Kopfzeile, z. B. „BPMN". */
  label: string;
  /** Ausgeschriebener Name für Tooltip und Popover, z. B. „generate_bpmn". */
  full?: string;
}

/** Was in einem Slot steht. `value` erscheint unter der Glyphe. */
export interface FlowCell {
  state: FlowState;
  value?: number | string | null;
  /** „hier sitzt der Job" — Akzentstrich unter dem Slot (Queue). */
  here?: boolean;
}

export type FlowCells = Partial<Record<string, FlowCell>>;

interface ChainGeometry {
  /** Kantenlänge der Glyphe. Default 18. */
  glyphSize?: number;
  /** Breite eines Slots. Default 24. */
  slotWidth?: number;
  /** Lücke zwischen zwei Slots — hier liegen die zwei Halbsegmente. Default 16. */
  gap?: number;
}

export interface MicroFlowProps extends ChainGeometry {
  slots: FlowSlotDef[];
  cells: FlowCells;
  vocabulary?: FlowVocabulary;
  /** Name des Objekts — Überschrift des Popovers, Präfix des `aria-label`. */
  label?: string;
  /** Popover je Kette (Default true). `false` = nur `title`. */
  popover?: boolean;
  className?: string;
}

function chainStyle({ glyphSize = 18, slotWidth = 24, gap = 16 }: ChainGeometry) {
  return {
    "--flow-glyph": `${glyphSize}px`,
    "--flow-slot": `${slotWidth}px`,
    "--flow-gap": `${gap}px`,
  } as CSSProperties;
}

/** Zelle eines Slots — fehlt sie, ist der Slot ein Ghost. */
export function cellOf(cells: FlowCells, slot: FlowSlotDef): FlowCell {
  return cells[slot.id] ?? { state: "ghost" };
}

/** Die Kette als ausgeschriebener Satz — `aria-label`, `title`, Popover-Fallback. */
export function describeChain(
  slots: FlowSlotDef[],
  cells: FlowCells,
  vocabulary: FlowVocabulary = "run",
): string {
  return slots
    .map((s) => {
      const c = cellOf(cells, s);
      const val = c.value == null ? "" : ` (${typeof c.value === "number" ? formatCount(c.value) : c.value})`;
      return `${s.full ?? s.label} ${stateWord(c.state, vocabulary)}${val}${c.here ? " — hier" : ""}`;
    })
    .join(" · ");
}

/** Die Zustände einer Kette in Slot-Reihenfolge. */
export function chainStates(slots: FlowSlotDef[], cells: FlowCells): FlowState[] {
  return slots.map((s) => cellOf(cells, s).state);
}

/* ── Kette ──────────────────────────────────────────────────────────────── */

export function MicroFlow({
  slots,
  cells,
  vocabulary = "run",
  label,
  popover = true,
  className,
  ...geometry
}: MicroFlowProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();

  const description = describeChain(slots, cells, vocabulary);
  const aria = label ? `${label} — Kette: ${description}` : `Kette: ${description}`;
  const style = chainStyle(geometry);
  const body = slots.map((slot) => {
    const cell = cellOf(cells, slot);
    return (
      <span key={slot.id} className="flow-slot" data-state={cell.state}>
        <StateGlyph state={cell.state} size={geometry.glyphSize ?? 18} />
        {cell.value != null && (
          <span className={`flow-slot-val${cell.value === 0 ? " is-zero" : ""}`}>
            {typeof cell.value === "number" ? formatCount(cell.value) : cell.value}
          </span>
        )}
        {cell.here && <span className="flow-slot-tick" aria-hidden="true" />}
      </span>
    );
  });

  if (!popover) {
    return (
      <span className={cx("flow-chain", className)} style={style} role="img" aria-label={aria} title={description}>
        {body}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={cx("flow-chain flow-chain--button", className)}
        style={style}
        aria-label={aria}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={description}
        onClick={(e) => {
          // Die Kette sitzt oft in einer klickbaren Zeile — der Popover ist eine
          // eigene, sekundäre Aktion und darf die Zeile nicht mit auslösen.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {body}
      </button>
      <Popover
        triggerRef={triggerRef}
        isOpen={open}
        onOpenChange={setOpen}
        placement="bottom"
        className="flow-popover"
      >
        <div role="dialog" aria-labelledby={headingId} className="flow-popover-in">
          <p className="flow-popover-head" id={headingId}>
            {label ?? "Kette"}
          </p>
          <ul className="flow-popover-list">
            {slots.map((slot) => {
              const cell = cellOf(cells, slot);
              return (
                <li key={slot.id} className="flow-popover-item" data-state={cell.state}>
                  <StateGlyph state={cell.state} size={14} />
                  <span className="flow-line flow-popover-slot">{slot.full ?? slot.label}</span>
                  <span className="flow-popover-word">{stateWord(cell.state, vocabulary)}</span>
                  <span className="flow-popover-val">
                    {cell.value == null
                      ? ""
                      : typeof cell.value === "number"
                        ? formatCount(cell.value)
                        : cell.value}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </Popover>
    </>
  );
}

/* ── Kopfzeile: die Slot-Namen stehen genau EINMAL ──────────────────────── */

export interface MicroFlowHeaderProps extends ChainGeometry {
  slots: FlowSlotDef[];
  /** Beschriftung der Leitspalte, z. B. „Run" / „Job". */
  children?: ReactNode;
  /** Beschriftung der rechten Spalte. */
  trail?: ReactNode;
  className?: string;
}

export function MicroFlowHeader({
  slots,
  children,
  trail,
  className,
  ...geometry
}: MicroFlowHeaderProps) {
  return (
    <div className={cx("flow-head", className)}>
      <span className="flow-head-lead" aria-hidden="true" />
      <span className="flow-head-main flow-line">{children}</span>
      <span className="flow-chain flow-chain--head" style={chainStyle(geometry)}>
        {slots.map((s) => (
          <span key={s.id} className="flow-slot flow-slot--head" title={s.full ?? s.label}>
            {s.label}
          </span>
        ))}
      </span>
      {trail !== undefined && <span className="flow-head-trail">{trail}</span>}
    </div>
  );
}

/* ── Roll-up-Glyphe der Leitspalte ──────────────────────────────────────── */

export interface RollUpGlyphProps {
  states: Iterable<FlowState>;
  size?: number;
  vocabulary?: FlowVocabulary;
  className?: string;
}

/**
 * Der führende Zustand einer Zeile, ganz links. Beantwortet „welche Zeile ist
 * kaputt", ohne die Kette lesen zu müssen. Der Text steckt im `aria-label` der
 * Zeile — die Glyphe selbst bleibt für Screenreader still.
 */
export function RollUpGlyph({ states, size = 14, vocabulary = "run", className }: RollUpGlyphProps) {
  const state = rollUpState(states);
  return (
    <span className={cx("flow-rollup", className)} data-state={state} title={stateWord(state, vocabulary)}>
      <StateGlyph state={state} size={size} />
    </span>
  );
}

/* ── Zeile ──────────────────────────────────────────────────────────────── */

export interface MicroFlowRowProps extends ChainGeometry {
  slots: FlowSlotDef[];
  cells: FlowCells;
  vocabulary?: FlowVocabulary;
  /** Titelzeile (wird gekürzt). */
  title: ReactNode;
  /** Untertitel (wird gekürzt). */
  subtitle?: ReactNode;
  /** Rechte Spalte, z. B. Dauer. */
  trail?: ReactNode;
  /** Klartext-Name des Objekts für `aria-label` und Popover-Kopf. */
  label: string;
  /**
   * Ziel der Zeile — macht den Kopf zu einem echten `<a href>`. Nur damit
   * funktionieren Mittelklick („in neuem Tab öffnen") und „Link-Adresse
   * kopieren"; in einer Status-Webapp öffnet man mehrere Läufe nebeneinander.
   */
  href?: string;
  /** Ohne `href`: macht den Kopf zum Button. Ohne beides bleibt die Zeile rein informativ. */
  onActivate?: () => void;
  /** Roll-up-Glyphe in der Leitspalte (Default true). */
  rollUp?: boolean;
  popover?: boolean;
  className?: string;
}

export function MicroFlowRow({
  slots,
  cells,
  vocabulary = "run",
  title,
  subtitle,
  trail,
  label,
  href,
  onActivate,
  rollUp = true,
  popover = true,
  className,
  ...geometry
}: MicroFlowRowProps) {
  const states = chainStates(slots, cells);
  const lead = rollUpState(states);

  return (
    <div className={cx("flow-row", className)} data-rollup={lead}>
      <span className="flow-row-lead">
        {rollUp && <RollUpGlyph states={states} vocabulary={vocabulary} />}
      </span>
      {/* Nur der Kopf ist interaktiv (<a> oder <button>); Kette und Trail sind
          Geschwister. Innerhalb des Kopfs sind alle Zeilen <span> — ein
          <button> nimmt nur Phrasing Content auf. */}
      <FlowHead
        href={href}
        onActivate={onActivate}
        className="flow-row-main"
        interactiveClassName={href != null ? "flow-row-main--link" : "flow-row-main--button"}
      >
        <span className="flow-line flow-row-title">{title}</span>
        {subtitle !== undefined && <span className="flow-line flow-row-sub">{subtitle}</span>}
      </FlowHead>
      <MicroFlow
        slots={slots}
        cells={cells}
        vocabulary={vocabulary}
        label={label}
        popover={popover}
        {...geometry}
      />
      {trail !== undefined && <span className="flow-row-trail">{trail}</span>}
    </div>
  );
}
